import React, { useEffect, useState, useRef } from "react";
import {
  Container, Card, CardBody, Row, Col, FormGroup, Label, Input,
  Button, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getListingById, createPendingApplication, submitListingFeedback } from "../../helpers/firestoreService";
import { finalizeRentalApplication } from "../../helpers/checkrApi";
import { getListingImageUrl } from "../../helpers/storageService";
import { useAuthUser } from "../../hooks/AuthProvider";
import { NotFoundPage } from "../System/SystemStatusPage";
import ApplicationFeePayment from "./ApplicationFeePayment";
import ListingChatWidget from "../../components/Common/ListingChatWidget";
import SeoMeta from "../../components/Common/SeoMeta";
import { getListingDisplayTitle } from "../../helpers/listingTitle";
import { logAnalyticsEvent } from "../../firebaseConfig";

const DEFAULT_APPLICATION_FEE = 50;
const LISTING_FEEDBACK_TOPICS = [
  ["photos", "More photos"],
  ["fees", "Fees"],
  ["pet_policy", "Pet policy"],
  ["tour_info", "Tour info"],
  ["application_process", "Application process"],
  ["neighborhood", "Neighborhood"],
];

// Shared validators for the applicant-entered fields — each rejects special
// characters/extra spaces and enforces a max length per the PM's screening
// requirements, so bad data never reaches Checkr or the property owner.
const NAME_RE = /^[A-Za-z'-]{1,40}$/;
const EMPLOYER_RE = /^[A-Za-z0-9](?:[A-Za-z0-9.,&'-]{0,58}[A-Za-z0-9])?$/;

const validateField = (field, value, { required } = {}) => {
  const v = (value ?? "").toString().trim();
  switch (field) {
    case "firstName":
    case "lastName": {
      const label = field === "firstName" ? "First name" : "Last name";
      if (!v) return `${label} is required.`;
      if (!NAME_RE.test(v)) return `${label} can only contain letters (no spaces or special characters), max 40 characters.`;
      return "";
    }
    case "phone": {
      if (!v) return required ? "Phone number is required." : "";
      const digits = v.replace(/\D/g, "");
      if (digits.length !== 10) return "Enter a valid 10-digit phone number.";
      return "";
    }
    case "employer": {
      if (!v) return required ? "Employer is required." : "";
      if (v.length > 60) return "Employer name is too long (max 60 characters).";
      if (!EMPLOYER_RE.test(v)) return "Employer contains invalid characters or extra spaces.";
      return "";
    }
    case "monthlyIncome": {
      if (!v) return required ? "Monthly income is required." : "";
      if (!/^\d{1,7}$/.test(v)) return "Monthly income must be numbers only (max 7 digits).";
      return "";
    }
    case "dob": {
      if (!v) return "Date of birth is required.";
      const d = new Date(`${v}T00:00:00`);
      if (Number.isNaN(d.getTime())) return "Enter a valid date.";
      if (d > new Date()) return "Date of birth can't be in the future.";
      const ageYears = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (ageYears < 18) return "Applicant must be at least 18 years old.";
      if (ageYears > 120) return "Enter a valid date of birth.";
      return "";
    }
    case "ssn": {
      if (!v) return ""; // optional
      const digits = v.replace(/\D/g, "");
      if (digits.length !== 9) return "SSN must be 9 digits (optional field).";
      return "";
    }
    default:
      return "";
  }
};

// ── Chip helper ───────────────────────────────────────────────────────────────
const Chip = ({ icon, label, subtle }) => (
  <span
    className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill small fw-medium me-2 mb-2"
    style={{ background: subtle ? "#f1f5f9" : "#eff2ff", color: subtle ? "#475569" : "#556ee6", border: "1px solid #e2e8f0" }}
  >
    {icon && <i className={`bx ${icon}`} style={{ lineHeight: 1 }} />}
    {label}
  </span>
);

const ListingActionsCard = ({ onApply, onRequestTour, showApply = true }) => (
  <Card className="shadow-sm border-0">
    <CardBody className="p-4">
      <h5 className="fw-bold mb-1">Interested in renting this home?</h5>
      <p className="text-muted small mb-3">Schedule a tour or apply online when you&apos;re ready.</p>
      <div className="d-grid gap-2">
        <Button color="primary" size="lg" onClick={onRequestTour}>
          <i className="bx bx-calendar me-1" />Request a Tour
        </Button>
        {showApply && (
          <Button color="primary" outline size="lg" onClick={onApply}>
            <i className="bx bx-file me-1" />Apply Now
          </Button>
        )}
      </div>
    </CardBody>
  </Card>
);

const ListingTrustCard = () => (
  <Card className="shadow-sm border-0">
    <CardBody className="p-4">
      <h5 className="fw-bold mb-3">Managed through Porcher</h5>
      <div className="d-flex flex-column gap-3 small">
        <div className="d-flex gap-2">
          <i className="bx bx-user-check text-primary mt-1" />
          <span>This listing was published by a landlord or property manager using Porcher.</span>
        </div>
        <div className="d-flex gap-2">
          <i className="bx bx-lock-alt text-primary mt-1" />
          <span>Your application is submitted through Porcher&apos;s secure renter flow.</span>
        </div>
        <div className="d-flex gap-2">
          <i className="bx bx-file text-primary mt-1" />
          <span>Application fees and screening details are disclosed before payment.</span>
        </div>
      </div>
      <div className="border-top mt-3 pt-3 d-flex flex-wrap gap-3 small">
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-analytics-event="listing_legal_link_click" data-analytics-destination="privacy_policy">Privacy Policy</a>
        <a href="/tenant-screening-policy" target="_blank" rel="noopener noreferrer" data-analytics-event="listing_legal_link_click" data-analytics-destination="tenant_screening_policy">Tenant Screening &amp; FCRA</a>
      </div>
      <p className="text-muted small mb-0 mt-3">Powered by Porcher.</p>
    </CardBody>
  </Card>
);

const ListingFeedbackCard = ({ listingId }) => {
  const [rating, setRating] = useState(0);
  const [topics, setTopics] = useState([]);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const toggleTopic = (topic) => {
    setTopics((current) =>
      current.includes(topic)
        ? current.filter((item) => item !== topic)
        : [...current, topic]
    );
  };

  const handleSubmit = async () => {
    setError("");
    if (!rating) {
      setError("Choose a star rating first.");
      return;
    }
    setSaving(true);
    try {
      await submitListingFeedback({ listingId, rating, topics, message });
      logAnalyticsEvent("listing_feedback_submit");
      setSubmitted(true);
    } catch (e) {
      console.error("Listing feedback error:", e);
      setError(e.message || "Feedback could not be submitted. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <Card className="shadow-sm border-0">
        <CardBody className="p-4 text-center">
          <i className="bx bx-check-circle text-success" style={{ fontSize: "2rem" }} />
          <h5 className="fw-semibold mt-2 mb-1">Thanks for the feedback</h5>
          <p className="text-muted small mb-0">It helps the landlord improve this listing for future renters.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <CardBody className="p-4">
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
          <div>
            <h5 className="fw-semibold mb-1">Was this listing clear?</h5>
            <p className="text-muted small mb-0">Share quick feedback for the landlord.</p>
          </div>
          <div className="d-flex gap-1" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="btn btn-link p-0"
                onClick={() => setRating(star)}
                aria-label={`Rate ${star} out of 5`}
                style={{ color: star <= rating ? "#f1b44c" : "#cbd5e1", fontSize: "1.35rem", lineHeight: 1 }}
              >
                <i className="bx bxs-star" />
              </button>
            ))}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          {LISTING_FEEDBACK_TOPICS.map(([value, label]) => {
            const selected = topics.includes(value);
            return (
              <button
                key={value}
                type="button"
                className={`btn btn-sm ${selected ? "btn-primary" : "btn-light border"}`}
                onClick={() => toggleTopic(value)}
              >
                {label}
              </button>
            );
          })}
        </div>

        <Input
          type="textarea"
          rows={3}
          maxLength={1000}
          placeholder="What would make this listing clearer?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        {error && <Alert color="danger" className="py-2 small mt-3 mb-0">{error}</Alert>}
        <div className="d-flex align-items-center justify-content-between gap-3 mt-3">
          <small className="text-muted">{message.length}/1000</small>
          <Button color="primary" size="sm" disabled={saving || !rating} onClick={handleSubmit}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-send me-1" />}
            Send Feedback
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const PET_LABELS = {
  "no-pets": "No Pets",
  "cats-ok": "Cats OK",
  "dogs-ok": "Dogs OK (under 25lb)",
  "pets-ok": "Pets Welcome",
  "negotiable": "Pets Negotiable",
};

const LEASE_LABELS = {
  "month-to-month": "Month-to-Month",
  "6-months": "6-Month Lease",
  "12-months": "1-Year Lease",
  "24-months": "2-Year Lease",
};

const LAUNDRY_LABELS = {
  "in-unit": "In-Unit Laundry",
  "in-building": "Laundry in Building",
  "laundromat": "Laundromat Nearby",
  "none": "No Laundry",
};

const PARKING_TYPE_LABELS = {
  "garage": "Garage Parking",
  "covered": "Covered Parking",
  "street": "Street Parking",
  "lot": "Parking Lot",
  "none": "No Parking",
};

const SMOKING_LABELS = {
  "no-smoking": "No Smoking",
  "outdoor-ok": "Outdoor Smoking OK",
  "allowed": "Smoking Allowed",
};

const NEARBY_TYPES = [
  { key: "school",      label: "Schools",     icon: "bxs-school" },
  { key: "restaurant",  label: "Restaurants", icon: "bx-food-menu" },
  { key: "grocery_or_supermarket", label: "Grocery", icon: "bx-cart" },
  { key: "transit_station",        label: "Transit",  icon: "bx-bus" },
  { key: "gym",         label: "Gyms",        icon: "bx-dumbbell" },
  { key: "park",        label: "Parks",        icon: "bxs-tree" },
];

const RentalApplicationPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthUser();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [requestTourToken, setRequestTourToken] = useState(0);
  // "form" -> collecting applicant info; "payment" -> collecting the fee for
  // the draft below; the real application isn't created until it succeeds.
  const [step, setStep] = useState("form");
  const [pendingApplicationId, setPendingApplicationId] = useState(null);
  const [finishing, setFinishing] = useState(false);

  const [thumbUrls, setThumbUrls] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  // Maps / Street View
  const mapRef = useRef(null);
  const streetRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const streetInstanceRef = useRef(null);
  const infoWindowRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const paymentSectionRef = useRef(null);
  const [mapTab, setMapTab] = useState("map"); // "map" | "street"
  const [nearbyTab, setNearbyTab] = useState("school");
  const [nearbyPlaces, setNearbyPlaces] = useState({});

  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", employer: "", monthlyIncome: "",
    dob: "", ssn: "", message: "",
  });
  const [consent, setConsent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [additionalApplicants, setAdditionalApplicants] = useState([]);
  const [adultErrors, setAdultErrors] = useState([]);
  const [minorsCount, setMinorsCount] = useState("0");
  const [confirmEmailsOpen, setConfirmEmailsOpen] = useState(false);

  const canonicalPath = `/rentals/${listingId}`;
  const listingTitle = listing ? getListingDisplayTitle(listing) : "Rental Home";
  const seoTitle = `${listingTitle} | Porcher Rentals`;
  const seoDescription = listing
    ? `View rent, property details, amenities, and availability for ${listingTitle} on Porcher.`
    : "View rental details, availability, and apply online with Porcher.";
  const isLegacyApplyPath = location.pathname.startsWith("/apply/");

  const addApplicant = () => setAdditionalApplicants((p) => [...p, { firstName: "", lastName: "", email: "", confirmEmail: "", relationship: "", phone: "", dob: "", ssn: "", employer: "", monthlyIncome: "" }]);
  const removeApplicant = (i) => {
    setAdditionalApplicants((p) => p.filter((_, idx) => idx !== i));
    setAdultErrors((p) => p.filter((_, idx) => idx !== i));
  };
  const setApplicant = (i, field, value) =>
    setAdditionalApplicants((p) => p.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));

  useEffect(() => {
    (async () => {
      try {
        const l = await getListingById(listingId);
        if (!l) setNotFound(true);
        else {
          setListing(l);
          // Populate nearby places from pre-computed Firestore data (no API calls needed)
          if (l.nearbyPlaces && typeof l.nearbyPlaces === "object") {
            setNearbyPlaces(l.nearbyPlaces);
          }
        }
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [listingId]);

  useEffect(() => {
    // Images belong to the listing itself (S3, uploaded when the PM created
    // this application link) — resolve each key to a fresh presigned URL.
    const imgs = Array.isArray(listing?.images) ? listing.images : [];
    if (imgs.length === 0) { setThumbUrls([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const urls = await Promise.all(
          imgs.map((img) => (img.key ? getListingImageUrl(img.key).catch(() => null) : null))
        );
        if (!cancelled) setThumbUrls(urls.filter(Boolean));
      } catch {
        if (!cancelled) setThumbUrls([]);
      }
    })();
    return () => { cancelled = true; };
  }, [listing?.images]);

  // Load Google Maps script once (reuse if already loaded by another page)
  useEffect(() => {
    const key = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY;
    if (!key || document.querySelector("[data-gmaps]")) return;
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true; s.defer = true; s.dataset.gmaps = "1";
    document.head.appendChild(s);
  }, []);

  // Init map + street view once we have an address and refs are mounted
  useEffect(() => {
    const addr = listing?.address;
    if (!addr) return;
    const init = () => {
      if (!window.google?.maps) return false;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status !== "OK" || !results[0]) return;
        const loc = results[0].geometry.location;

        if (mapRef.current && !mapInstanceRef.current) {
          const map = new window.google.maps.Map(mapRef.current, {
            center: loc, zoom: 15,
            mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
          });
          new window.google.maps.Marker({ position: loc, map, title: addr });
          mapInstanceRef.current = map;
        }
        if (streetRef.current && !streetInstanceRef.current) {
          streetInstanceRef.current = new window.google.maps.StreetViewPanorama(streetRef.current, {
            position: loc, pov: { heading: 34, pitch: 10 },
            addressControl: false, fullscreenControl: false,
          });
        }
      });
      return true;
    };
    if (!init()) {
      const t = setInterval(() => { if (init()) clearInterval(t); }, 300);
      return () => clearInterval(t);
    }
  }, [listing?.address]);

  const handleNearbyTab = (type) => setNearbyTab(type);
  const visibleNearbyPlaces = (type) => (nearbyPlaces[type] || [])
    .filter((place) => Number(place.rating) > 4)
    .slice(0, 5);

  const openApplication = () => {
    logAnalyticsEvent("listing_apply_click", { listing_active: listing.active !== false });
    setApplicationOpen(true);
    requestAnimationFrame(() => {
      document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const requestTour = () => {
    logAnalyticsEvent("listing_tour_request_click", { listing_active: listing.active !== false });
    setRequestTourToken((token) => token + 1);
  };

  useEffect(() => {
    if (step !== "payment" || !pendingApplicationId) return;
    const frame = requestAnimationFrame(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      paymentSectionRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [step, pendingApplicationId]);

  // Distance helpers
  const haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const formatDist = (km) => {
    const mi = km * 0.621371;
    return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(1)} mi`;
  };

  // Focus map on a nearby place; switch to map tab first if needed
  const focusOnPlace = (place) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    const pos = { lat: place.lat, lng: place.lng };
    mapInstanceRef.current.panTo(pos);
    mapInstanceRef.current.setZoom(17);
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }
    const mapsUrl = place.place_id
      ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}`
      : `https://www.google.com/maps/search/${encodeURIComponent(place.name)}`;
    infoWindowRef.current.setContent(
      `<div style="padding:4px 2px;min-width:160px;font-family:inherit">` +
      `<div style="font-weight:600;font-size:0.88rem;margin-bottom:3px">${place.name}</div>` +
      (place.vicinity ? `<div style="font-size:0.75rem;color:#64748b;margin-bottom:6px">${place.vicinity}</div>` : "") +
      `<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="font-size:0.78rem;color:#556ee6;text-decoration:none">View on Google Maps →</a>` +
      `</div>`
    );
    infoWindowRef.current.setPosition(pos);
    infoWindowRef.current.open(mapInstanceRef.current);
  };

  const handlePlaceClick = (place) => {
    if (!place.lat || !place.lng || !window.google?.maps) return;
    if (mapTab === "map" && mapInstanceRef.current) {
      // Already on map tab and map is ready — focus immediately
      focusOnPlace(place);
    } else {
      // Need to switch to map tab first; execute focus after render
      pendingFocusRef.current = place;
      setMapTab("map");
    }
  };

  // After switching to map tab, execute any pending focus
  useEffect(() => {
    if (mapTab !== "map" || !pendingFocusRef.current || !mapInstanceRef.current) return;
    const place = pendingFocusRef.current;
    pendingFocusRef.current = null;
    focusOnPlace(place);
  }, [mapTab]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (fieldErrors[field]) setFieldErrors((fe) => ({ ...fe, [field]: "" }));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const applicationFeeDollars = listing?.applicationFee > 0 ? Number(listing.applicationFee) : DEFAULT_APPLICATION_FEE;
  // Every adult occupant (18+) gets their own Checkr screening — the
  // primary applicant pays the whole household's fee up front, one time,
  // right here, so nobody else ever has to log in or pay separately.
  const validAdultOccupants = additionalApplicants.filter((p) => p.firstName.trim());
  const totalApplicants = 1 + validAdultOccupants.length;
  const totalFeeDollars = applicationFeeDollars * totalApplicants;

  const handleSubmit = async () => {
    setError("");
    if (listing?.active === false) {
      setError("This rental is no longer accepting applications.");
      return;
    }
    const requiredFields = ["firstName", "lastName", "phone", "employer", "monthlyIncome", "dob"];
    const errs = {};
    ["firstName", "lastName", "phone", "employer", "monthlyIncome", "dob", "ssn"].forEach((field) => {
      const msg = validateField(field, form[field], { required: requiredFields.includes(field) });
      if (msg) errs[field] = msg;
    });
    if (!consent) errs.consent = "You must agree before submitting your application.";

    // Same fields (and same validators) as the primary applicant — each
    // adult occupant is screened exactly like the primary is. Employer/income
    // are optional for co-applicants (unlike the primary, who must supply them).
    const adultRequired = ["firstName", "lastName", "phone", "dob"];
    const adultErrs = additionalApplicants.map((p) => {
      if (!p.firstName.trim()) return {};
      const e = {};
      [...adultRequired, "ssn", "employer", "monthlyIncome"].forEach((field) => {
        const msg = validateField(field, p[field], { required: adultRequired.includes(field) });
        if (msg) e[field] = msg;
      });
      if (!EMAIL_RE.test(p.email.trim())) e.email = "Enter a valid email — Checkr emails their screening invite there directly.";
      else if (p.email.trim().toLowerCase() !== (p.confirmEmail || "").trim().toLowerCase()) e.confirmEmail = "Emails don't match.";
      return e;
    });

    // Every occupant needs a unique, personal email — this is how each one
    // gets (and completes) their OWN Checkr screening invite. Without this
    // check someone can list themselves twice with one email and pass every
    // "screening" as themselves (the incident this validation exists for).
    const primaryEmail = (user?.email || "").trim().toLowerCase();
    const emailCounts = {};
    additionalApplicants.forEach((p) => {
      const e = p.email.trim().toLowerCase();
      if (e) emailCounts[e] = (emailCounts[e] || 0) + 1;
    });
    additionalApplicants.forEach((p, i) => {
      if (!p.firstName.trim() || adultErrs[i].email) return;
      const e = p.email.trim().toLowerCase();
      if (!e) return;
      if (e === primaryEmail) {
        adultErrs[i].email = "This matches your sign-in email — this occupant needs their own email to complete their own screening.";
      } else if (emailCounts[e] > 1) {
        adultErrs[i].email = "This email is used by another occupant — each adult needs a separate email for their own screening.";
      }
    });

    setAdultErrors(adultErrs);
    if (adultErrs.some((e) => Object.keys(e).length > 0)) {
      errs.additionalApplicants = "Please fix the highlighted fields for your additional occupants.";
    }

    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError("Please fix the highlighted fields before continuing.");
      return;
    }

    // Every occupant's own email matters — one last chance to double-check
    // (and edit) before we charge the card and send out screening invites.
    if (validAdultOccupants.length > 0) {
      setConfirmEmailsOpen(true);
      return;
    }
    await submitApplication();
  };

  const submitApplication = async () => {
    setConfirmEmailsOpen(false);
    setSaving(true);
    try {
      // Just a draft — no real (PM-visible) application exists until the fee
      // below succeeds, so nothing is "submitted" if payment never goes through.
      const id = await createPendingApplication(listingId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: (user.email || "").toLowerCase(),
        applicantUid: user.uid,
        phone: form.phone.trim(),
        employer: form.employer.trim(),
        monthlyIncome: form.monthlyIncome.trim(),
        dob: form.dob,
        message: form.message,
        propertyId: listing.propertyId,
        applicationFeeAmount: Math.round(totalFeeDollars * 100),
        minorsCount: Math.max(0, parseInt(minorsCount, 10) || 0),
        // SSN is deliberately left out — same "never touches Firestore" rule
        // as the primary applicant's own SSN, forwarded client-side instead
        // once the application exists (see screenAdultOccupants below).
        additionalApplicants: validAdultOccupants.map((p) => ({
          firstName: p.firstName.trim(),
          lastName: p.lastName.trim(),
          name: `${p.firstName.trim()} ${p.lastName.trim()}`.trim(),
          email: p.email.trim().toLowerCase(),
          relationship: p.relationship.trim(),
          phone: p.phone.trim(),
          dob: p.dob,
          employer: p.employer.trim(),
          monthlyIncome: p.monthlyIncome.trim(),
        })),
      });
      logAnalyticsEvent("rental_application_form_submit");
      setPendingApplicationId(id);
      setStep("payment");
    } catch (e) {
      console.error("Application submit error:", e);
      setError("Failed to submit application. Please try again.");
    } finally { setSaving(false); }
  };


  // Called right after Stripe confirms the fee payment. Sends everything —
  // including DOB/SSN for the primary and every adult occupant, still held
  // only in this component's memory — to finalizeRentalApplication in ONE
  // request, which verifies the payment, promotes the draft, seeds the
  // co-applicant sub-docs, and creates every household member's Checkr order
  // synchronously. Replaces the old design of polling for an async webhook
  // to do this piecemeal, which could silently lose the race and leave a
  // co-applicant's screening never started (see repo memory for the
  // incident this replaced). SSN is still never written to Firestore.
  const handleFeePaid = async (paymentIntentId) => {
    setFinishing(true);
    try {
      const result = await finalizeRentalApplication(
        pendingApplicationId,
        paymentIntentId,
        { dob: form.dob, ssn: form.ssn },
        validAdultOccupants.map((p) => ({ email: p.email.trim().toLowerCase(), dob: p.dob, ssn: p.ssn })),
      );
      logAnalyticsEvent("rental_application_fee_paid");
      const anyMissed = result.primary?.error || (result.coApplicants || []).some((c) => c.error);
      if (anyMissed) sessionStorage.setItem("porcher_coapplicant_screening_incomplete", "1");
    } catch (e) {
      console.warn("Could not finalize application/screening (can retry from My Applications):", e);
      sessionStorage.setItem("porcher_coapplicant_screening_incomplete", "1");
    } finally {
      navigate("/my-applications");
    }
  };

  // ── States ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
      <SeoMeta title="Rental Home | Porcher" description={seoDescription} canonicalPath={canonicalPath} noIndex={isLegacyApplyPath} />
      <Spinner color="primary" />
    </div>
  );

  if (notFound) return (
    <>
      <SeoMeta title="Rental Not Found | Porcher" description="This rental listing is not available." canonicalPath={canonicalPath} noIndex />
      <NotFoundPage />
    </>
  );

  const avail = listing.availableDate
    ? new Date(listing.availableDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  // ── Main listing page ───────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <SeoMeta title={seoTitle} description={seoDescription} canonicalPath={canonicalPath} noIndex={isLegacyApplyPath || listing.active === false} />
      {listing.active !== false && (
        <ListingChatWidget listingId={listingId} user={user} requestTourToken={requestTourToken} />
      )}
      {/* Main content */}
      <Container className="py-2">
        <Row>
          {/* ── Left: listing details ─────────────────────────────────────── */}
          <Col lg={8} className="pe-lg-4 mb-4">

            {/* Listing header */}
            <div className="mb-4">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                <div className="flex-grow-1">
                  <span
                    className="d-inline-block px-3 py-2 rounded-pill text-white fw-bold mb-2"
                    style={{ background: listing.active === false ? "#64748b" : "#556ee6", fontSize: "0.72rem", lineHeight: 1 }}
                  >
                    {listing.active === false ? "NO LONGER AVAILABLE" : "FOR RENT"}
                  </span>
                  <h1 className="h2 fw-bold mb-1" style={{ color: "#1a1a2e" }}>{listingTitle}</h1>
                  {listing.address && (
                    <p className="text-muted mb-0">
                      <i className="bx bx-map me-1" />{listing.address}
                    </p>
                  )}
                </div>
                {listing.monthlyRent > 0 && (
                  <div className="text-lg-end">
                    <div className="fw-bold fs-3" style={{ color: "#556ee6" }}>
                      ${Number(listing.monthlyRent).toLocaleString()}
                    </div>
                    <small className="text-muted">per month</small>
                  </div>
                )}
              </div>
              <div className="d-flex flex-wrap mt-3">
                {listing.bedrooms !== undefined && listing.bedrooms !== "" && (
                  <Chip icon="bx-bed" label={Number(listing.bedrooms) === 0 ? "Studio" : `${listing.bedrooms} Bed`} />
                )}
                {listing.bathrooms !== undefined && listing.bathrooms !== "" && <Chip icon="bx-bath" label={`${listing.bathrooms} Bath`} />}
                {listing.sqft && <Chip icon="bx-expand-alt" label={`${Number(listing.sqft).toLocaleString()} sq ft`} />}
              </div>
            </div>

            {/* ── In-page photo gallery (eBay style) ── */}
            {thumbUrls.length > 0 && (
              <div className="mb-4">
                {/* Main image */}
                <div className="position-relative mb-2" style={{ borderRadius: 10, overflow: "hidden", background: "#111", aspectRatio: "16/9" }}>
                  <img
                    src={thumbUrls[activeImg]}
                    alt="Property"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {/* Prev / Next arrows */}
                  {thumbUrls.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setActiveImg((p) => (p - 1 + thumbUrls.length) % thumbUrls.length)}
                        style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="bx bx-chevron-left" style={{ fontSize: "1.4rem" }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveImg((p) => (p + 1) % thumbUrls.length)}
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%", width: 36, height: 36, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="bx bx-chevron-right" style={{ fontSize: "1.4rem" }} />
                      </button>
                      {/* Counter badge */}
                      <span style={{ position: "absolute", bottom: 10, right: 12, background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: "0.8rem" }}>
                        {activeImg + 1} / {thumbUrls.length}
                      </span>
                    </>
                  )}
                </div>
                {/* Thumbnail strip */}
                {thumbUrls.length > 1 && (
                  <div className="d-flex gap-2 overflow-auto pb-1">
                    {thumbUrls.map((url, i) => (
                      <div
                        key={i}
                        onClick={() => setActiveImg(i)}
                        style={{
                          width: 80, height: 58, flexShrink: 0, borderRadius: 6, overflow: "hidden", cursor: "pointer",
                          border: i === activeImg ? "2px solid #556ee6" : "2px solid transparent",
                          opacity: i === activeImg ? 1 : 0.65, transition: "opacity 0.15s, border-color 0.15s",
                        }}
                      >
                        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {listing.active === false && (
              <Alert color="secondary" className="mb-4">
                <h5 className="alert-heading mb-1">This rental is no longer available</h5>
                <p className="mb-2">The property is not accepting applications or tour requests.</p>
                <Button color="primary" size="sm" onClick={() => navigate("/rentals")}>Browse Available Rentals</Button>
              </Alert>
            )}

            {/* Key stats chips */}
            <div className="mb-4 pb-3 border-bottom">
              {listing.leaseTerm && <Chip icon="bx-calendar-check" label={LEASE_LABELS[listing.leaseTerm] || listing.leaseTerm} />}
              {avail && <Chip icon="bx-calendar" label={`Available ${avail}`} />}
              {listing.petPolicy && <Chip icon="bx-paw" label={PET_LABELS[listing.petPolicy] || listing.petPolicy} />}
              {listing.petDeposit > 0 && <Chip icon="bx-credit-card" label={`$${Number(listing.petDeposit).toLocaleString()} pet deposit`} subtle />}
              {listing.petRent > 0 && <Chip icon="bx-dollar-circle" label={`$${Number(listing.petRent).toLocaleString()}/mo pet rent`} subtle />}
              {listing.deposit > 0 && <Chip icon="bx-credit-card" label={`$${Number(listing.deposit).toLocaleString()} deposit`} subtle />}
              {listing.parkingIncluded && <Chip icon="bx-car" label="Parking Included" subtle />}
            </div>

            {/* Mobile listing actions */}
            {listing.active !== false && (
              <div className="d-lg-none mb-4">
                <ListingActionsCard onApply={openApplication} onRequestTour={requestTour} />
                <div className="mt-3">
                  <ListingTrustCard />
                </div>
              </div>
            )}

            {/* Description */}
            {listing.description && (
              <div className="mb-4">
                <h5 className="fw-semibold mb-2">About this rental</h5>
                <p style={{ lineHeight: 1.75, color: "#374151", whiteSpace: "pre-wrap" }}>{listing.description}</p>
              </div>
            )}

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div className="mb-4">
                <h5 className="fw-semibold mb-2">Amenities</h5>
                <div>
                  {listing.amenities.map((a) => (
                    <Chip key={a} icon="bx-check" label={a} subtle />
                  ))}
                </div>
              </div>
            )}

            {/* Appliances */}
            {listing.appliances?.length > 0 && (
              <div className="mb-4">
                <h5 className="fw-semibold mb-2">Appliances</h5>
                <div>
                  {listing.appliances.map((a) => <Chip key={a} icon="bx-plug" label={a} subtle />)}
                </div>
              </div>
            )}

            {/* Utilities */}
            {listing.utilitiesIncluded?.length > 0 && (
              <div className="mb-4 pb-3 border-bottom">
                <div>
                  <h5 className="fw-semibold mb-2">Utilities Included</h5>
                  <div>
                    {listing.utilitiesIncluded.map((u) => <Chip key={u} icon="bx-plug" label={u} subtle />)}
                  </div>
                </div>
              </div>
            )}

            {/* Additional details row */}
            {(listing.laundry || listing.parkingType || listing.floor || listing.yearBuilt || listing.smokingPolicy || listing.furnished) && (
              <div className="mb-4 pb-3 border-bottom">
                <h5 className="fw-semibold mb-2">Details</h5>
                <div>
                  {listing.furnished && <Chip icon="bx-home-smile" label={{ furnished: "Furnished", unfurnished: "Unfurnished", partially: "Partially Furnished" }[listing.furnished] || listing.furnished} subtle />}
                  {listing.laundry && <Chip icon="bx-water" label={LAUNDRY_LABELS[listing.laundry] || listing.laundry} subtle />}
                  {listing.parkingType && <Chip icon="bx-car" label={PARKING_TYPE_LABELS[listing.parkingType] || listing.parkingType} subtle />}
                  {listing.parkingSpaces && <Chip icon="bx-car" label={`${listing.parkingSpaces} parking space${listing.parkingSpaces > 1 ? "s" : ""}`} subtle />}
                  {listing.parkingFee > 0 && <Chip icon="bx-dollar" label={`$${Number(listing.parkingFee).toLocaleString()}/mo parking`} subtle />}
                  {listing.floor && <Chip icon="bx-building" label={`Floor ${listing.floor}`} subtle />}
                  {listing.yearBuilt && <Chip icon="bx-time" label={`Built ${listing.yearBuilt}`} subtle />}
                  {listing.smokingPolicy && <Chip icon="bx-no-entry" label={SMOKING_LABELS[listing.smokingPolicy] || listing.smokingPolicy} subtle />}
                </div>
              </div>
            )}

            {/* Lease qualifications */}
            {(listing.minIncome || listing.creditScore || listing.backgroundCheck || listing.occupancyLimit) && (
              <div className="mb-4 pb-3 border-bottom">
                <h5 className="fw-semibold mb-2">Lease Qualifications</h5>
                <div className="d-flex flex-column gap-2">
                  {listing.minIncome && (
                    <div className="d-flex gap-2 align-items-center">
                      <i className="bx bx-dollar-circle text-muted" />
                      <span className="small"><strong>Income:</strong> {listing.minIncome}</span>
                    </div>
                  )}
                  {listing.creditScore && (
                    <div className="d-flex gap-2 align-items-center">
                      <i className="bx bx-credit-card text-muted" />
                      <span className="small"><strong>Credit Score:</strong> {listing.creditScore}+</span>
                    </div>
                  )}
                  {listing.backgroundCheck && (
                    <div className="d-flex gap-2 align-items-center">
                      <i className="bx bx-user-check text-muted" />
                      <span className="small">Background check required</span>
                    </div>
                  )}
                  {listing.occupancyLimit && (
                    <div className="d-flex gap-2 align-items-center">
                      <i className="bx bx-group text-muted" />
                      <span className="small"><strong>Max occupants:</strong> {listing.occupancyLimit}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom landlord cards */}
            {(listing.customCards || []).length > 0 && (
              <div className="mb-4 pb-3 border-bottom">
                {(listing.customCards || [])
                  .slice()
                  .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                  .map((card, i) => (
                    <div key={i} className="mb-3">
                      <h5 className="fw-semibold mb-2">
                        {card.icon && <i className={`bx ${card.icon} me-2 text-primary`} />}
                        {card.title}
                      </h5>
                      <p style={{ lineHeight: 1.75, color: "#374151", whiteSpace: "pre-wrap" }}>{card.body}</p>
                    </div>
                  ))}
              </div>
            )}

            {/* Map + Street View */}
            {listing.address && (
              <div className="mb-4">
                <h5 className="fw-semibold mb-2">Location</h5>
                {/* Tab switcher */}
                <div className="d-flex gap-2 mb-2">
                  {["map", "street"].map((tab) => (
                    <button key={tab} type="button"
                      onClick={() => setMapTab(tab)}
                      className="btn btn-sm"
                      style={{
                        background: mapTab === tab ? "#556ee6" : "#f1f5f9",
                        color: mapTab === tab ? "#fff" : "#475569",
                        border: "none", borderRadius: 6, fontWeight: 500,
                      }}>
                      <i className={`bx ${tab === "map" ? "bx-map" : "bx-street-view"} me-1`} />
                      {tab === "map" ? "Map" : "Street View"}
                    </button>
                  ))}
                </div>
                <div style={{ borderRadius: 10, overflow: "hidden", height: 280, position: "relative" }}>
                  <div ref={mapRef} style={{ width: "100%", height: "100%", display: mapTab === "map" ? "block" : "none" }} />
                  <div ref={streetRef} style={{ width: "100%", height: "100%", display: mapTab === "street" ? "block" : "none" }} />
                  {!window.google?.maps && (
                    <div style={{ position: "absolute", inset: 0, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Spinner color="secondary" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Nearby places */}
            {listing.address && (
              <div className="mb-4 pb-3 border-bottom">
                <h5 className="fw-semibold mb-2">What's Nearby</h5>
                {/* Category tabs */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {NEARBY_TYPES.map(({ key, label, icon }) => (
                    <button key={key} type="button"
                      onClick={() => handleNearbyTab(key)}
                      className="btn btn-sm"
                      style={{
                        background: nearbyTab === key ? "#556ee6" : "#f1f5f9",
                        color: nearbyTab === key ? "#fff" : "#475569",
                        border: "none", borderRadius: 6, fontWeight: 500,
                      }}>
                      <i className={`bx ${icon} me-1`} />{label}
                    </button>
                  ))}
                </div>
                {visibleNearbyPlaces(nearbyTab).length === 0 ? (
                  <p className="text-muted small mb-0 fst-italic">No nearby data available.</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {(() => {
                      const center = nearbyPlaces._center;
                      const places = visibleNearbyPlaces(nearbyTab);
                      if (center) {
                        places.sort((a, b) => {
                          const da = (a.lat && a.lng) ? haversineKm(center.lat, center.lng, a.lat, a.lng) : Infinity;
                          const db = (b.lat && b.lng) ? haversineKm(center.lat, center.lng, b.lat, b.lng) : Infinity;
                          return da - db;
                        });
                      }
                      return places.map((place, i) => {
                        const r = place.rating ? Math.round(place.rating * 2) / 2 : null;
                        const dist = (center && place.lat && place.lng)
                          ? formatDist(haversineKm(center.lat, center.lng, place.lat, place.lng))
                          : null;
                        const hasCoords = !!(place.lat && place.lng && window.google?.maps);
                        return (
                          <div key={i}
                            onClick={() => hasCoords && handlePlaceClick(place)}
                            className="d-flex align-items-center gap-2 p-2 rounded-2"
                            style={{ background: "#f8f9fb", cursor: hasCoords ? "pointer" : "default", transition: "background 0.15s" }}
                            onMouseEnter={(e) => { if (hasCoords) e.currentTarget.style.background = "#eff2ff"; }}
                            onMouseLeave={(e) => e.currentTarget.style.background = "#f8f9fb"}
                          >
                            <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                              style={{ width: 36, height: 36, background: "#eff2ff" }}>
                              <i className={`bx ${NEARBY_TYPES.find((t) => t.key === nearbyTab)?.icon || "bx-map-pin"}`}
                                style={{ color: "#556ee6", fontSize: "1.1rem" }} />
                            </div>
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="fw-medium small text-truncate" style={{ color: "#1e293b" }}>{place.name}</div>
                              {place.vicinity && <div className="text-muted" style={{ fontSize: "0.72rem" }}>{place.vicinity}</div>}
                              {r !== null && (
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  {[1,2,3,4,5].map((star) => (
                                    <i key={star}
                                      className={r >= star ? "bx bxs-star" : r >= star - 0.5 ? "bx bxs-star-half" : "bx bx-star"}
                                      style={{ color: "#f6c23e", fontSize: "0.75rem" }}
                                    />
                                  ))}
                                  <span style={{ fontSize: "0.72rem", color: "#64748b", marginLeft: 2 }}>{place.rating.toFixed(1)}</span>
                                </div>
                              )}
                            </div>
                            <div className="d-flex flex-column align-items-end flex-shrink-0 gap-1">
                              {dist && <span className="badge rounded-pill" style={{ background: "#f1f5f9", color: "#475569", fontWeight: 500, fontSize: "0.7rem" }}>{dist}</span>}
                              {hasCoords && <i className="bx bx-map-pin" style={{ color: "#94a3b8", fontSize: "0.85rem" }} />}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            {listing.active !== false && (
              <div className="mb-4">
                <ListingFeedbackCard listingId={listingId} />
              </div>
            )}

          </Col>

          {/* ── Right: apply form ─────────────────────────────────────────── */}
          <Col lg={4} id="apply-form" style={{ scrollMarginTop: 96 }}>
            <div style={{ position: "sticky", top: 80 }}>
              {listing.active !== false && (
                <div className="d-none d-lg-block mb-3">
                  <ListingActionsCard
                    onApply={openApplication}
                    onRequestTour={requestTour}
                    showApply={!applicationOpen && step !== "payment"}
                  />
                  <div className="mt-3">
                    <ListingTrustCard />
                  </div>
                </div>
              )}
              {listing.active === false ? (
                <Card className="shadow-sm border-0">
                  <CardBody className="p-4">
                    <h5 className="fw-bold mb-2">No longer available</h5>
                    <p className="text-muted small">This rental is not accepting applications or tour requests.</p>
                    <Button color="primary" block onClick={() => navigate("/rentals")}>Browse Available Rentals</Button>
                  </CardBody>
                </Card>
              ) : step === "payment" ? (
                <div ref={paymentSectionRef} tabIndex={-1} style={{ scrollMarginTop: 96, outline: "none" }}>
                  <Card className="shadow-sm border-0">
                    <CardBody className="p-4">
                      {finishing ? (
                        <div className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="text-muted small mt-3 mb-0">Finishing up your application…</p>
                        </div>
                      ) : (
                        <>
                          <h5 className="fw-bold mb-1">Pay the application fee</h5>
                          <p className="text-muted small mb-2">
                            Last step — pay the ${applicationFeeDollars} application fee per adult to submit your application for review.
                          </p>
                          <p className="text-muted small mb-3">
                            <i className="bx bx-envelope me-1" />
                            You will get an email from Checkr to complete your screening and monitor the status on Porcher. You may need to add additional documents in Porcher.
                          </p>
                          <ApplicationFeePayment
                            pendingApplicationId={pendingApplicationId}
                            applicantEmail={user?.email}
                            onSuccess={handleFeePaid}
                          />
                        </>
                      )}
                    </CardBody>
                  </Card>
                </div>
              ) : applicationOpen ? (
                <Card className="shadow-sm border-0">
                  <CardBody className="p-4">
                    <h5 className="fw-bold mb-1">Apply for this unit</h5>
                    <p className="text-muted small mb-3">
                      {user ? (
                        <>Applying as <strong>{user.email}</strong> — we&apos;ll also use this email for your background screening invitation.</>
                      ) : (
                        <>Complete the form below, then sign in to submit your application.</>
                      )}
                    </p>
                    <Alert color="light" className="py-2 small mb-3">
                      <i className="bx bx-lock-alt me-1" />
                      Porcher shares your application with the landlord or property manager for this rental.
                      Screening and payment details are shown before you submit.
                    </Alert>

                    {/* Move-in cost summary */}
                    {(listing.monthlyRent > 0 || listing.deposit > 0) && (
                      <div className="rounded p-3 mb-3" style={{ background: "#f8f9fb", border: "1px solid #e2e8f0" }}>
                        <p className="small fw-semibold mb-2 text-muted">Estimated Move-in Costs</p>
                        <div className="d-flex flex-column gap-1">
                          {listing.monthlyRent > 0 && <div className="d-flex justify-content-between small"><span className="text-muted">First Month</span><span className="fw-medium">${Number(listing.monthlyRent).toLocaleString()}</span></div>}
                          {listing.deposit > 0 && <div className="d-flex justify-content-between small"><span className="text-muted">Security Deposit</span><span className="fw-medium">${Number(listing.deposit).toLocaleString()}</span></div>}
                          <div className="d-flex justify-content-between small border-top pt-1 mt-1">
                            <span className="fw-semibold">Total Due at Move-in</span>
                            <span className="fw-bold" style={{ color: "#556ee6" }}>${(Number(listing.monthlyRent || 0) + Number(listing.deposit || 0)).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Application fee explainer */}
                    <Alert color="info" className="py-2 small mb-3">
                      <i className="bx bx-info-circle me-1" />
                      A <strong>${applicationFeeDollars} application fee per adult</strong> is required to verify your income and run a
                      criminal background check.
                    </Alert>

                    {error && <Alert color="danger" className="py-2">{error}</Alert>}

                    <Row>
                      <Col xs={6}>
                        <FormGroup>
                          <Label className="small fw-medium">First Name <span className="text-danger">*</span></Label>
                          <Input placeholder="Jane" maxLength={40} invalid={!!fieldErrors.firstName} value={form.firstName} onChange={set("firstName")} />
                          {fieldErrors.firstName && <div className="text-danger small mt-1">{fieldErrors.firstName}</div>}
                        </FormGroup>
                      </Col>
                      <Col xs={6}>
                        <FormGroup>
                          <Label className="small fw-medium">Last Name <span className="text-danger">*</span></Label>
                          <Input placeholder="Smith" maxLength={40} invalid={!!fieldErrors.lastName} value={form.lastName} onChange={set("lastName")} />
                          {fieldErrors.lastName && <div className="text-danger small mt-1">{fieldErrors.lastName}</div>}
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label className="small fw-medium">Phone <span className="text-danger">*</span></Label>
                      <Input type="tel" placeholder="(555) 000-0000" maxLength={14} invalid={!!fieldErrors.phone} value={form.phone} onChange={set("phone")} />
                      {fieldErrors.phone && <div className="text-danger small mt-1">{fieldErrors.phone}</div>}
                    </FormGroup>
                    <Row>
                      <Col xs={7}>
                        <FormGroup>
                          <Label className="small fw-medium">Employer <span className="text-danger">*</span></Label>
                          <Input placeholder="Company" maxLength={60} invalid={!!fieldErrors.employer} value={form.employer} onChange={set("employer")} />
                          {fieldErrors.employer && <div className="text-danger small mt-1">{fieldErrors.employer}</div>}
                        </FormGroup>
                      </Col>
                      <Col xs={5}>
                        <FormGroup>
                          <Label className="small fw-medium">Mo. Income <span className="text-danger">*</span></Label>
                          <Input type="number" min="0" maxLength={7} placeholder="$" invalid={!!fieldErrors.monthlyIncome} value={form.monthlyIncome} onChange={set("monthlyIncome")} />
                          {fieldErrors.monthlyIncome && <div className="text-danger small mt-1">{fieldErrors.monthlyIncome}</div>}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <FormGroup>
                          <Label className="small fw-medium">Date of Birth <span className="text-danger">*</span></Label>
                          <Input type="date" invalid={!!fieldErrors.dob} value={form.dob} onChange={set("dob")} />
                          {fieldErrors.dob && <div className="text-danger small mt-1">{fieldErrors.dob}</div>}
                        </FormGroup>
                      </Col>
                      <Col xs={6}>
                        <FormGroup>
                          <Label className="small fw-medium">SSN <span className="text-muted fw-normal">(optional)</span></Label>
                          <Input placeholder="•••-••-••••" maxLength={11} invalid={!!fieldErrors.ssn} value={form.ssn} onChange={set("ssn")} />
                          {fieldErrors.ssn && <div className="text-danger small mt-1">{fieldErrors.ssn}</div>}
                        </FormGroup>
                      </Col>
                    </Row>
                    <p className="text-muted small mt-n2 mb-3">
                      <i className="bx bx-shield-quarter me-1" />
                      Adding your SSN may make the background check faster — we do not store this information.
                    </p>
                    <FormGroup>
                      <Label className="small fw-medium">Message to Landlord</Label>
                      <Input type="textarea" rows={3} placeholder="Introduce yourself, ask questions…" value={form.message} onChange={set("message")} />
                    </FormGroup>

                    {/* Additional applicants */}
                    <div className="border-top pt-3 mt-1 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="fw-medium text-muted">Additional Occupants</small>
                      </div>
                      <FormGroup className="mb-2">
                        <Label className="small fw-medium">Occupants Under 18</Label>
                        <Input
                          type="number"
                          bsSize="sm"
                          min="0"
                          max="20"
                          style={{ maxWidth: 100 }}
                          value={minorsCount}
                          onChange={(e) => setMinorsCount(e.target.value)}
                        />
                        <small className="text-muted">Just a headcount — no form or fee required for minors.</small>
                      </FormGroup>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="fw-medium text-muted">Other Adult Occupants (18+)</small>
                        <Button color="light" size="sm" className="py-0 px-2" onClick={addApplicant}>
                          <i className="bx bx-plus" /> Add
                        </Button>
                      </div>
                      <p className="text-muted small mb-2">
                        Each additional adult is a $50 background screening — they can sign in
                        to Porcher and complete their background screening with the email you
                        provide below.
                      </p>
                      {additionalApplicants.map((p, i) => {
                        const err = adultErrors[i] || {};
                        return (
                          <div key={i} className="border rounded p-2 mb-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <small className="fw-semibold text-muted">Adult Occupant {i + 1}</small>
                              <button type="button" className="btn btn-sm btn-outline-danger py-0 px-2" style={{ lineHeight: 1.6 }} onClick={() => removeApplicant(i)}>×</button>
                            </div>
                            <Row>
                              <Col xs={6}>
                                <Input bsSize="sm" className="mb-1" placeholder="First name" invalid={!!err.firstName} value={p.firstName} onChange={(e) => setApplicant(i, "firstName", e.target.value)} />
                                {err.firstName && <div className="text-danger small mb-1">{err.firstName}</div>}
                              </Col>
                              <Col xs={6}>
                                <Input bsSize="sm" className="mb-1" placeholder="Last name" invalid={!!err.lastName} value={p.lastName} onChange={(e) => setApplicant(i, "lastName", e.target.value)} />
                                {err.lastName && <div className="text-danger small mb-1">{err.lastName}</div>}
                              </Col>
                            </Row>
                            <Input type="email" bsSize="sm" className="mb-1" placeholder="Email" invalid={!!err.email} value={p.email} onChange={(e) => setApplicant(i, "email", e.target.value)} />
                            {err.email && <div className="text-danger small mb-1">{err.email}</div>}
                            <Input type="email" bsSize="sm" className="mb-1" placeholder="Confirm email" invalid={!!err.confirmEmail} value={p.confirmEmail} onChange={(e) => setApplicant(i, "confirmEmail", e.target.value)} />
                            {err.confirmEmail && <div className="text-danger small mb-1">{err.confirmEmail}</div>}
                            <Row>
                              <Col xs={6}>
                                <Input bsSize="sm" className="mb-1" placeholder="Phone" invalid={!!err.phone} value={p.phone} onChange={(e) => setApplicant(i, "phone", e.target.value)} />
                                {err.phone && <div className="text-danger small mb-1">{err.phone}</div>}
                              </Col>
                              <Col xs={6}>
                                <Input type="date" bsSize="sm" className="mb-1" invalid={!!err.dob} value={p.dob} onChange={(e) => setApplicant(i, "dob", e.target.value)} />
                                {err.dob && <div className="text-danger small mb-1">{err.dob}</div>}
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={7}>
                                <Input bsSize="sm" className="mb-1" placeholder="Employer (optional)" invalid={!!err.employer} value={p.employer} onChange={(e) => setApplicant(i, "employer", e.target.value)} />
                                {err.employer && <div className="text-danger small mb-1">{err.employer}</div>}
                              </Col>
                              <Col xs={5}>
                                <Input type="number" min="0" bsSize="sm" className="mb-1" placeholder="Mo. income" invalid={!!err.monthlyIncome} value={p.monthlyIncome} onChange={(e) => setApplicant(i, "monthlyIncome", e.target.value)} />
                                {err.monthlyIncome && <div className="text-danger small mb-1">{err.monthlyIncome}</div>}
                              </Col>
                            </Row>
                            <Input bsSize="sm" className="mb-1" placeholder="SSN (optional)" maxLength={11} invalid={!!err.ssn} value={p.ssn} onChange={(e) => setApplicant(i, "ssn", e.target.value)} />
                            {err.ssn && <div className="text-danger small mb-1">{err.ssn}</div>}
                            <Input type="select" bsSize="sm" value={p.relationship} onChange={(e) => setApplicant(i, "relationship", e.target.value)}>
                              <option value="">Relationship</option>
                              <option>Spouse / Partner</option>
                              <option>Roommate</option>
                              <option>Dependent / Child</option>
                              <option>Other</option>
                            </Input>
                          </div>
                        );
                      })}
                      {fieldErrors.additionalApplicants && <div className="text-danger small mt-1">{fieldErrors.additionalApplicants}</div>}
                    </div>

                    <div
                      className="mb-3 p-2 bg-light rounded"
                      style={{ cursor: "pointer" }}
                      onClick={() => { setConsent((v) => !v); if (fieldErrors.consent) setFieldErrors((fe) => ({ ...fe, consent: "" })); }}
                    >
                      <FormGroup check className="mb-0">
                        <Input type="checkbox" id="consentCheckbox" readOnly checked={consent} />
                        <Label check for="consentCheckbox" className="small mb-0" style={{ cursor: "pointer" }}>
                          I agree to the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Privacy Policy</a> and{" "}
                          <a href="/tenant-screening-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Tenant Screening &amp; FCRA Policy</a>, and consent to
                          sharing this information with a secure third-party provider to verify my identity, income, and background.
                        </Label>
                      </FormGroup>
                      {fieldErrors.consent && <div className="text-danger small mt-1">{fieldErrors.consent}</div>}
                    </div>

                    {totalApplicants > 1 && (
                      <div className="d-flex justify-content-between align-items-center mb-2 small">
                        <span className="text-muted">Application fee (${applicationFeeDollars} × {totalApplicants} applicants)</span>
                        <span className="fw-semibold">${totalFeeDollars.toLocaleString()}</span>
                      </div>
                    )}

                    <Button color="primary" block disabled={!user || saving} onClick={handleSubmit}>
                      {saving ? <Spinner size="sm" /> : user ? <><i className="bx bx-send me-1" />Start Application</> : <><i className="bx bx-lock-alt me-1" />Sign in to continue</>}
                    </Button>
                    <p className="text-muted small text-center mt-2 mb-0" style={{ paddingBottom: 96 }}>
                      Simple and quick — usually takes 5 minutes to complete, though the background check itself can take
                      anywhere from a few minutes to a few days.
                    </p>
                  </CardBody>
                </Card>
              ) : null}
            </div>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={confirmEmailsOpen} toggle={() => setConfirmEmailsOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmEmailsOpen(false)}>Confirm occupant emails</ModalHeader>
        <ModalBody>
          <p className="mb-3">
            This application will start <strong>{totalApplicants} separate background screenings</strong> —
            one for you and one for each additional adult occupant. Every occupant will receive their own
            Checkr screening invite at the email listed below, and must complete it themselves.
          </p>
          <ul className="list-unstyled mb-3">
            <li className="d-flex justify-content-between border-bottom py-2">
              <span>You</span>
              <span className="fw-semibold">{user?.email}</span>
            </li>
            {validAdultOccupants.map((p, i) => (
              <li key={i} className="d-flex justify-content-between border-bottom py-2">
                <span>{p.firstName.trim()} {p.lastName.trim()}</span>
                <span className="fw-semibold">{p.email.trim().toLowerCase()}</span>
              </li>
            ))}
          </ul>
          <Alert color="warning" className="py-2 small mb-0">
            <i className="bx bx-error-circle me-1" />
            Each adult must complete their screening using their own email. If any of these
            look wrong, go back and edit them before continuing.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmEmailsOpen(false)}>Go Back &amp; Edit</Button>
          <Button color="primary" disabled={saving} onClick={submitApplication}>
            {saving ? <Spinner size="sm" /> : "Confirm & Continue"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default RentalApplicationPage;
